<template>
    <div class="invoice-status-changer">
        <span
            v-if="!isAdmin || availableTransitions.length === 0"
            class="status-badge"
            :class="statusClass"
        >{{ statusLabel }}</span>

        <div class="status-dropdown-container position-relative" v-else>
            <div class="dropdown-backdrop" v-if="dropdownOpen" @click="dropdownOpen = false"></div>

            <div
                class="custom-status-select"
                :class="[statusClass, { 'is-loading': loading, 'is-open': dropdownOpen }]"
                @click="!loading && (dropdownOpen = !dropdownOpen)"
            >
                <span>{{ statusLabel }}</span>
                <i v-if="loading" class="fa fa-spin fa-spinner text-muted"></i>
                <i v-else class="fa fa-chevron-down arrow-icon"></i>
            </div>

            <div class="custom-dropdown-menu" v-if="dropdownOpen">
                <div
                    class="dropdown-option"
                    :class="{ 'active': !targetStatus }"
                    @click="handleSelect(invoice.status)"
                >
                    {{ labelFor(invoice.status) }}
                </div>
                <div
                    class="dropdown-option"
                    :class="{ 'active': pendingStatus === s }"
                    v-for="s in availableTransitions"
                    :key="s"
                    @click="handleSelect(s)"
                >
                    {{ labelFor(s) }}
                </div>
            </div>
        </div>

        <KvModal
            ref="confirmStatusModal"
            id="confirm-status-changer-modal"
            @close="onModalClose"
        >
            <template #title>{{ modalConfig.title }}</template>
            <template #subtitle v-if="modalConfig.subtitle">{{ modalConfig.subtitle }}</template>
            <template #body>
                <span>{{ modalConfig.message }}</span>
            </template>
            <template #footer="{ toggle }">
                <kv-button variant="outlined" @click="toggle(false)">{{ t('invoices-cancel') }}</kv-button>
                <kv-button
                    :variant="modalConfig.variant === 'danger' ? 'danger' : 'filled'"
                    :loading="loading"
                    @click="confirmChange"
                >
                    {{ modalConfig.confirmText }}
                </kv-button>
            </template>
        </KvModal>

        <payment-confirm-modal
            ref="payModal"
            modal-id="invoice-status-pay-modal"
            :invoice="invoice"
            :title="modalConfig.title"
            :subtitle="modalConfig.subtitle"
            :hint="modalConfig.message"
            :loading="loading"
            :show-fines-fields="isOverdue"
            @confirm="onPayConfirm"
            @close="onPayModalClose"
        />
    </div>
</template>

<script>
import keviApi from '../../keviApi';
import KvModal from '../../components/modal/modal.vue';
import PaymentConfirmModal from './payment-confirm-modal.vue';
import { computeInvoiceStatus, STATUS_LABELS, extractApiError } from '../helpers';

const TRANSITIONS = {
    draft:     ['pending', 'paid'],
    pending:   ['paid', 'cancelled'],
    paid:      [],
    cancelled: []
};

export default {
    name: 'invoice-status-changer',
    components: { KvModal, PaymentConfirmModal },
    props: {
        invoice:       { type: Object,  required: true },
        isAdmin:       { type: Boolean, default: false }
    },
    data() {
        return {
            pendingStatus: this.invoice.status,
            loading:       false,
            error:         null,
            dropdownOpen:  false,
            targetStatus:  null
        };
    },
    watch: {
        'invoice.status'(val) {
            this.pendingStatus = val;
            this.error = null;
        },
        error(val) {
            this.$emit('error', val);
        }
    },
    computed: {
        computedStatus() {
            return computeInvoiceStatus(this.invoice.status, this.invoice.due_date);
        },
        statusClass() {
            return `status-${this.computedStatus}`;
        },
        statusLabel() {
            return this.t('invoices-status_' + this.computedStatus);
        },
        availableTransitions() {
            return TRANSITIONS[this.invoice.status] || [];
        },
        modalConfig() {
            if (!this.targetStatus) return { title: '', message: '', confirmText: this.t('invoices-confirm'), variant: 'primary' };

            const label    = this.labelFor(this.targetStatus);
            const invLabel = this.invoice.title || `${this.t('invoices-invoice')} #${this.invoice.id}`;

            const cfg = {
                title:       this.t('invoices-change_status_to').replace('{label}', label),
                subtitle:    this.t('invoices-change_status_subtitle').replace('{invLabel}', invLabel),
                message:     this.t('invoices-change_status_confirm').replace('{label}', label),
                confirmText: this.t('invoices-yes_change'),
                variant:     'primary'
            };

            if (this.targetStatus === 'pending') {
                cfg.title       = this.t('invoices-wait_payment_title');
                cfg.subtitle    = this.t('invoices-wait_payment_subtitle').replace('{invLabel}', invLabel);
                cfg.message     = this.t('invoices-check_bank_slip_hint');
                cfg.confirmText = this.t('invoices-yes_change_status');
            } else if (this.targetStatus === 'cancelled') {
                cfg.variant     = 'danger';
                cfg.subtitle    = this.t('invoices-will_be_cancelled_subtitle').replace('{invLabel}', invLabel);
                cfg.message     = this.t('invoices-cancel_invoice_hint');
                cfg.confirmText = this.t('invoices-yes_cancel_invoice');
            } else if (this.targetStatus === 'paid') {
                cfg.variant     = 'success';
                cfg.subtitle    = this.t('invoices-will_be_paid_subtitle').replace('{invLabel}', invLabel);
                cfg.message     = this.t('invoices-mark_as_paid_hint');
                cfg.confirmText = this.t('invoices-yes_mark_as_paid');
            }

            return cfg;
        },
        isOverdue() {
            return this.computedStatus === 'overdue';
        }
    },
    methods: {
        labelFor(status) {
            return this.t('invoices-status_' + status);
        },
        handleSelect(newStatus) {
            this.dropdownOpen = false;
            if (newStatus === this.invoice.status) return;

            if (newStatus === 'pending' && !this.invoice.bank_slip_url) {
                this.error = this.t('invoices-attach_bank_slip_error');
                return;
            }

            this.error        = null;
            this.targetStatus = newStatus;

            if (newStatus === 'paid') {
                this.$nextTick(() => this.$refs.payModal && this.$refs.payModal.open());
            } else {
                this.$nextTick(() => {
                    if (this.$refs.confirmStatusModal) {
                        this.$refs.confirmStatusModal.open();
                    }
                });
            }
        },
        onModalClose() {
            this.targetStatus = null;
        },
        onPayModalClose() {
            this.targetStatus = null;
        },
        async onPayConfirm({ paid_at, interest_fines, total_paid }) {
            if (!this.targetStatus) return;
            this.loading = true;
            this.error   = null;
            try {
                const payload = { status: 'paid', paid_at, interest_fines, total_paid };
                const options  = keviApi.getOptions(`invoices/${this.invoice.id}/status`, 'PATCH', payload);
                const response = await this.$http(options);
                if (response.status === 200) {
                    this.$emit('status-changed', 'paid');
                    this.targetStatus = null;
                    this.$refs.payModal && this.$refs.payModal.close();
                } else {
                    this.error = extractApiError(response, this.t('invoices-update_status_error'));
                }
            } catch (e) {
                this.error = extractApiError(e, this.t('invoices-update_status_connection_error'));
            } finally {
                this.loading = false;
            }
        },
        async confirmChange() {
            const newStatus = this.targetStatus;
            if (!newStatus || newStatus === this.invoice.status) return;

            this.loading = true;
            this.error   = null;

            try {
                const payload = { status: newStatus };
                const options  = keviApi.getOptions(`invoices/${this.invoice.id}/status`, 'PATCH', payload);
                const response = await this.$http(options);

                if (response.status === 200) {
                    this.$emit('status-changed', newStatus);
                    this.targetStatus = null;
                    if (this.$refs.confirmStatusModal) {
                        this.$refs.confirmStatusModal.close();
                    }
                } else {
                    this.error = extractApiError(response, this.t('invoices-update_status_error'));
                    this.pendingStatus = this.invoice.status;
                }
            } catch (e) {
                this.error = extractApiError(e, this.t('invoices-update_status_connection_error'));
                this.pendingStatus = this.invoice.status;
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import '../helpers/status-colors';

.invoice-status-changer {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
}

.status-badge {
    font-size: 13px;
    font-weight: 500;
    padding: 4px 12px;
    border-radius: 100px;
    border: 1px solid transparent;
    @include invoice-status-modifiers;
}

.custom-status-select {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 100px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
    gap: 8px;
    border: 1px solid transparent;

    &.is-open  { box-shadow: 0 0 0 2px rgba(0,0,0,0.05); }
    &.is-loading { cursor: not-allowed; opacity: 0.7; }

    .arrow-icon {
        font-size: 10px;
        opacity: 0.6;
        transition: transform 0.2s;
    }
    &.is-open .arrow-icon { transform: rotate(180deg); }

    @include invoice-status-modifiers;
}

.dropdown-backdrop {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    z-index: 99;
}

.status-dropdown-container { position: relative; }

.custom-dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 150px;
    background: #fff;
    border-radius: 12px;
    border: 1px solid #F3F4F6;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
    z-index: 100;
    overflow: hidden;

    .dropdown-option {
        padding: 10px 16px;
        cursor: pointer;
        font-size: 13px;
        color: #374151;
        transition: background 0.15s;

        &:hover  { background: #F9FAFB; }
        &.active { font-weight: 600; color: #111827; background: #F3F4F6; }
    }
}

.status-error-info {
    font-size: 12px;
    .extra-small { font-size: 11px; }
}

</style>
